import parse from "date-fns/parse";
import format from "date-fns/format";
import addMinutes from "date-fns/addMinutes";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import isValid from "date-fns/isValid";

/** Must match TimePickerInput timeIntervals */
const TIME_PICKER_INTERVAL_MINUTES = 30;

/**
 * @param {string} string - Date/time string with space separator
 * @returns {string[]}
 */
const separateDateParts = (string) => {
    if (string == null || string === "") return [];
    return String(string).split(" ");
};

/**
 * @param {string} string - Date string (e.g. dd/mm/yyyy or dd-mm-yyyy)
 * @param {boolean} [switchOrder] - If true, interpret as mm/dd/yyyy
 * @returns {string|undefined}
 */
const getDate = (string, switchOrder) => {
    if (string == null || string === "") return undefined;
    string = String(string).replace(/\//g, "-");

    if (string !== "?") {
        string = separateDateParts(string)[0];
        string = string.split("-");

        if (switchOrder) {
            return `${string[2]}-${string[0]}-${string[1]}`;
        } else {
            return `${string[2]}-${string[1]}-${string[0]}`;
        }
    }
};

//Dates with ? values will break the site
/**
 * @param {string} string - Date/time string (space-separated date and time)
 * @returns {string|undefined}
 */
const getTime = (string) => {
    if (string == null || string === "") return undefined;
    if (string !== "?") {
        return separateDateParts(string)[1];
    }
};

/**
 * @param {string} string - Time string (e.g. "HH:mm:ss")
 * @returns {string}
 */
const extractHoursAndMinutes = (string) => {
    if (string == null || string === "") return "";
    const parts = String(string).split(":");
    if (parts.length < 3) return String(string);

    return `${parts[1]}:${parts[2]}`;
};

/**
 * Sets the minimum date for a date picker based on the previous leg's date
 * @param {number} currentIndex - The current leg index
 * @param {Array} journeyData - Array of journey data
 * @returns {Date|undefined} - The minimum date or undefined if no previous leg
 */
const setMinDate = (currentIndex, journeyData) => {
    if (currentIndex > 0 && journeyData[currentIndex - 1]?.date) {
        const prevDateParts = journeyData[currentIndex - 1].date.split("/");
        return new Date(prevDateParts[2], prevDateParts[1] - 1, prevDateParts[0]);
    }
    return undefined;
};

/**
 * Sets the maximum date for a date picker based on the next leg's date
 * @param {number} currentIndex - The current leg index
 * @param {Array} journeyData - Array of journey data
 * @returns {Date|undefined} - The maximum date or undefined if no next leg
 */
const setMaxDate = (currentIndex, journeyData) => {
    if (currentIndex < journeyData.length - 1 && journeyData[currentIndex + 1]?.date) {
        const nextDateParts = journeyData[currentIndex + 1].date.split("/");
        return new Date(nextDateParts[2], nextDateParts[1] - 1, nextDateParts[0]);
    }
    return undefined;
};

const TIME_FORMAT_24H = "HH:mm";
const TIME_FORMAT_12H = "hh:mmaaaaa'm'";

/**
 * Parses a journey time string into a Date (time portion only).
 * @param {string} timeStr
 * @param {boolean} [is24Hour=true]
 * @returns {Date|undefined}
 */
const parseJourneyTimeToDate = (timeStr, is24Hour = true) => {
    if (!timeStr) return undefined;
    const formats = is24Hour ? [TIME_FORMAT_24H] : [TIME_FORMAT_12H, "hh:mmaa"];
    for (const fmt of formats) {
        try {
            const parsed = parse(timeStr, fmt, new Date());
            if (!Number.isNaN(parsed.getTime())) return parsed;
        } catch {
            // try next format
        }
    }
    return undefined;
};

/**
 * Sets the minimum selectable time so a leg can't depart before the previous one.
 * Next leg must depart strictly after the previous (one picker interval later).
 *
 * The constraint applies when this leg is on the same day as the previous leg. It
 * also applies when this leg's date isn't set yet: the earliest date it can take
 * (enforced by setMinDate) is the previous leg's day, so we constrain the time as
 * if it were the same day until a date is chosen. Only once a strictly later date
 * is selected is the time left unrestricted. Without this fallback, filling the
 * time before the date would let the user pick a time earlier than the previous leg.
 * @param {number} currentIndex
 * @param {Array} journeyData
 * @param {boolean} [is24Hour=true]
 * @returns {Date|undefined}
 */
const setMinTime = (currentIndex, journeyData, is24Hour = true) => {
    if (currentIndex <= 0) return undefined;

    const prevLeg = journeyData[currentIndex - 1];
    const currentLeg = journeyData[currentIndex];

    if (!prevLeg?.date || !prevLeg?.time) return undefined;
    // A confirmed later date needs no time restriction; an empty date falls through
    // and is constrained against the previous leg (its earliest possible day).
    if (currentLeg?.date && prevLeg.date !== currentLeg.date) return undefined;

    const prevDeparture = parseJourneyTimeToDate(prevLeg.time, is24Hour);
    if (!prevDeparture) return undefined;

    return addMinutes(prevDeparture, TIME_PICKER_INTERVAL_MINUTES);
};

/**
 * Converts 12h time string (hh:mmaa) to 24h (HH:mm).
 * @param {string} time - Time in 12h format e.g. "02:30pm"
 * @returns {string} Time in HH:mm or original if parsing fails
 */
const formatTimeTo24h = (time) => {
    if (!time) return time;
    try {
        const parsed = parse(time, "hh:mmaa", new Date());
        return format(parsed, "HH:mm", { awareOfUnicodeTokens: true });
    } catch {
        return time;
    }
};

/**
 * Returns today's date as dd/MM/yyyy.
 */
const formatToday = () => {
    return format(new Date(), "dd/MM/yyyy", { awareOfUnicodeTokens: true });
};

/**
 * Converts date string from dd/MM/yyyy to yyyy-MM-dd (API format).
 * @param {string} dateStr - Date in dd/MM/yyyy format
 * @returns {string} Date in yyyy-MM-dd format
 */
const dateStringToApiFormat = (dateStr) => {
    if (!dateStr) return dateStr;
    try {
        const parsed = parse(dateStr, "dd/MM/yyyy", new Date());
        return format(parsed, "yyyy-MM-dd", { awareOfUnicodeTokens: true });
    } catch {
        return dateStr;
    }
};

/**
 * Converts dd/MM/yyyy or dd-MM-yyyy string to sortable integer for comparison.
 * @param {string} value - Date string with / or - separators
 * @param {boolean} [switchOrder=false] - If true, use MM/dd order for the middle/first parts
 * @returns {number|undefined} Integer like 20240115 or undefined if invalid
 */
const dateStringToSortableNumber = (value, switchOrder = false) => {
    if (!value) return undefined;
    const parts = value.replace(/-/g, "/").split("/");
    if (parts.length !== 3) return undefined;
    if (switchOrder) {
        return parseInt(`${parts[2]}${parts[0]}${parts[1]}`, 10);
    }
    return parseInt(`${parts[2]}${parts[1]}${parts[0]}`, 10);
};

const LEG_DATE_INPUT_FORMATS = [
    "yyyy-MM-dd",
    "dd/MM/yyyy",
    "dd/MM/yy",
    "d MMMM yyyy",
    "dd MMMM yyyy",
    "d MMM yyyy",
    "dd MMM yyyy",
];

/**
 * Parses a leg date from common API/contract formats.
 * @param {string} value
 * @returns {Date|null}
 */
const parseLegDate = (value) => {
    if (value == null || value === "" || value === "?") return null;

    const normalizedIso = getDate(value);
    if (normalizedIso) {
        try {
            const parsed = parse(normalizedIso, "yyyy-MM-dd", new Date());
            if (isValid(parsed)) return parsed;
        } catch {
            // try text formats below
        }
    }

    const normalized = String(value).replace(/,\s*/g, " ").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        try {
            const parsed = parse(normalized, "yyyy-MM-dd", new Date());
            if (isValid(parsed)) return parsed;
        } catch {
            // try text formats below
        }
    }

    for (const inputFormat of LEG_DATE_INPUT_FORMATS) {
        try {
            const parsed = parse(normalized, inputFormat, new Date());
            if (!Number.isNaN(parsed.getTime()) && isValid(parsed)) return parsed;
        } catch {
            // try next format
        }
    }

    return null;
};

/**
 * Formats a leg departure date to dd/MM/yy (or dd/MM/yyyy).
 * Accepts yyyy-MM-dd, dd/MM/yyyy, dd/MM/yy, and long text (e.g. "29 May 2026", "18 May, 2026").
 * @param {string} departureDate
 * @param {boolean} [shortYear=true] - If true, outputs dd/MM/yy instead of dd/MM/yyyy
 * @returns {string} Formatted date or original string if parsing fails
 */
const formatLegDepartureDate = (departureDate, shortYear = true) => {
    if (!departureDate) return "";
    const parsed = parseLegDate(departureDate);
    if (parsed) {
        const outputFormat = shortYear ? "dd/MM/yy" : "dd/MM/yyyy";
        return format(parsed, outputFormat, { awareOfUnicodeTokens: true });
    }
    return departureDate;
};

/**
 * Formats a dashboard timestamp: date as dd/MM/yy, time unchanged when present.
 * @param {string} dateTime - e.g. "23/10/2022 11:30"
 * @returns {string}
 */
const formatDashboardDateTime = (dateTime) => {
    if (!dateTime) return "";
    const parts = String(dateTime).trim().split(/\s+/);
    const formattedDate = formatLegDepartureDate(parts[0]);
    if (parts.length > 1) {
        return `${formattedDate} ${parts.slice(1).join(" ")}`;
    }
    return formattedDate;
};

/**
 * Formats leg duration from "HH:mm:ss" to "HH:mm" format.
 * @param {string|number} duration - Duration string (e.g. "02:30:00") or number
 * @returns {string} Formatted duration or original value if not a time string
 */
const formatLegDuration = (duration) => {
    if (!duration) return duration;
    if (typeof duration === "string" && duration.includes(":")) {
        try {
            return extractHoursAndMinutes(duration);
        } catch (e) {
            return duration;
        }
    }
    return duration;
};

/**
 * Parses a date or date-time value to a Date for calendar-day comparison.
 * @param {string} value - yyyy-MM-dd, dd/MM/yyyy, dd-MM-yyyy, text month formats, or date-time with space separator
 * @returns {Date|null}
 */
const parseDateForComparison = (value) => parseLegDate(value);

/**
 * Converts a time string to minutes since midnight.
 * @param {string} time
 * @returns {number|null}
 */
const parseTimeToMinutes = (time) => {
    if (!time) return null;

    const trimmed = String(time).trim();
    const match24 = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (match24) {
        return parseInt(match24[1], 10) * 60 + parseInt(match24[2], 10);
    }

    try {
        const as24 = formatTimeTo24h(trimmed);
        const match12 = as24.match(/^(\d{1,2}):(\d{2})/);
        if (match12) {
            return parseInt(match12[1], 10) * 60 + parseInt(match12[2], 10);
        }
    } catch {
        return null;
    }

    return null;
};

/**
 * Returns how many calendar days after departure the arrival occurs (0 if same day or unknown).
 * Falls back to comparing departure/arrival times when explicit arrival date is missing or same-day.
 * @param {string} departureDate
 * @param {string} [arrivalDate]
 * @param {string} [departureTime]
 * @param {string} [arrivalTime]
 * @returns {number}
 */
const getArrivalDayOffset = (departureDate, arrivalDate, departureTime, arrivalTime) => {
    const departure = parseDateForComparison(departureDate);
    const arrival = parseDateForComparison(arrivalDate);

    if (departure && arrival) {
        const diff = differenceInCalendarDays(arrival, departure);
        if (diff > 0) return diff;
    }

    if (departure && departureTime && arrivalTime) {
        const departureMinutes = parseTimeToMinutes(departureTime);
        const arrivalMinutes = parseTimeToMinutes(arrivalTime);
        if (departureMinutes != null && arrivalMinutes != null && arrivalMinutes < departureMinutes) {
            return 1;
        }
    }

    return 0;
};

export {
    getDate,
    getTime,
    extractHoursAndMinutes,
    setMinDate,
    setMaxDate,
    setMinTime,
    formatTimeTo24h,
    formatToday,
    dateStringToApiFormat,
    dateStringToSortableNumber,
    formatLegDepartureDate,
    formatDashboardDateTime,
    formatLegDuration,
    parseDateForComparison,
    getArrivalDayOffset,
};
